import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";
import { sfx } from "@/lib/sounds";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("missing");
      return;
    }
    getPaymentStatus(sessionId)
      .then((r) => {
        if (r.payment_status === "paid") {
          sfx.fanfare();
          setState("paid");
          setTimeout(() => navigate("/", { replace: true }), 2400);
        } else {
          setState("unpaid");
        }
      })
      .catch(() => setState("unpaid"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-ink px-6 text-center text-paper">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 180, damping: 15 }}
        className="card-edge flex w-full max-w-md flex-col items-center gap-4 px-8 py-12"
      >
        {state === "checking" && (
          <>
            <Loader2 size={48} className="animate-spin text-pumpkin" />
            <div className="font-display text-3xl">Counting your coins...</div>
            <p className="text-sm text-paper/60">Confirming your payment with Stripe</p>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="rounded-full bg-slime p-5 text-ink shadow-glow-slime">
              <CheckCircle2 size={44} />
            </span>
            <div data-testid="payment-success-title" className="font-display text-4xl text-slime text-glow-slime">
              Payment complete!
            </div>
            <p className="text-sm text-paper/70">
              Your magic is unlocked — taking you back to the Vault...
            </p>
          </>
        )}
        {(state === "unpaid" || state === "missing") && (
          <>
            <span className="rounded-full bg-candy/20 p-5 text-candy">
              <AlertTriangle size={44} />
            </span>
            <div className="font-display text-4xl text-candy">Hmm, no confirmation yet</div>
            <p className="text-sm text-paper/70">
              If you just paid, give it a moment and try again — you won't be
              charged twice for the same unlock.
            </p>
            <button
              data-testid="payment-back-btn"
              onClick={() => navigate("/", { replace: true })}
              className="mt-2 rounded-full bg-pumpkin px-7 py-3 font-bold text-ink shadow-glow-orange"
            >
              Back to the Vault
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
